// Match Engine 2.5 availability rules. Every site that asks "is this player
// suspended", "how many yellows", "who is sent off" or "can he go into the XI"
// calls these, so the rules live in one place.
//
// The fields are absent before the ME 2.5 backend deploy. Absent reads as
// "not yet": no yellows, no ban, nobody sent off. With them absent every
// predicate answers exactly as the injury check alone did.

export const isInjured = (player) => player?.form_range === -1;

// `suspension` counts the league or cup matches still to miss.
export const isSuspended = (player) => Number(player?.suspension) > 0;

// Yellows this season toward a ban (0 to 2 in practice).
export const yellowCardCount = (player) => {
  const count = Number(player?.yellow_cards);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

// Ids listed in either side's red cards for the running match. A player id
// belongs to one club, so there is no need to work out which side is ours.
export const sentOffIds = (matchRunning) => {
  const ids = new Set();
  if (!matchRunning || typeof matchRunning !== 'object') return ids;
  ['home_team_statistics', 'away_team_statistics'].forEach((side) => {
    const cards = matchRunning[side]?.red_cards;
    if (!Array.isArray(cards)) return;
    cards.forEach((card) => {
      if (card?.player?._id) ids.add(String(card.player._id));
    });
  });
  return ids;
};

export const isSentOff = (matchRunning, playerId) =>
  Boolean(playerId) && sentOffIds(matchRunning).has(String(playerId));

// Can this player be added to the XI, or swapped into it from the bench?
export const canEnterLineup = (player) =>
  Boolean(player) && !isInjured(player) && !isSuspended(player);

export const SENT_OFF_COPY = 'Sent off: stays in the lineup until full time.';

// On a control that would bring a bench player into a sent-off player's slot.
export const SENT_OFF_SLOT_COPY = 'The player in this position was sent off: he stays in the lineup until full time.';

const matchesToMiss = (player) => {
  const matches = Number(player?.suspension);
  return matches > 1
    ? `the next ${matches} league or cup matches`
    : 'the next league or cup match';
};

export const suspendedCopy = (player) => `Suspended: misses ${matchesToMiss(player)}.`;

// Right-hand side of a "Suspended" label.
export const suspendedLabelCopy = (player) => `Misses ${matchesToMiss(player)}`;

export const yellowCardsLabel = (count) => `${count} yellow card${count === 1 ? '' : 's'}`;

export const YELLOW_CARD_RULE_COPY = 'Three in league and cup matches mean a one-match ban';

export const yellowCardsCopy = (count) =>
  `${yellowCardsLabel(count)} this season. ${YELLOW_CARD_RULE_COPY}.`;

// POST /team/strategy refuses a lineup with a bare 403. Name the players the
// board can identify: suspended players in the XI being saved, and our sent-off
// players missing from it. Injured players are not named, because one already
// in the saved XI is allowed and the board can't tell which ones the server
// holds. The fallback lists only reasons that apply with today's API as well.
export const lineupRefusedCopy = (xi = [], strategy = {}) => {
  const playerName = (player) => player.name || `${player.first_name} ${player.last_name}`;
  const inXi = new Set(xi.map((player) => String(player._id)));
  const sentOff = sentOffIds(strategy.matchRunning);
  const named = [
    ...xi.filter(isSuspended).map((player) => `${playerName(player)} (suspended)`),
    ...(strategy.allTeam || [])
      .filter((player) => sentOff.has(String(player._id)) && !inXi.has(String(player._id)))
      .map((player) => `${playerName(player)} (sent off: must stay in the lineup)`),
  ];
  return named.length
    ? `This lineup isn’t allowed: ${named.join(', ')}.`
    : 'This lineup isn’t allowed. Check for injured players or players who are no longer at the club.';
};
